/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-18 08:54:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import type { PredictiveBundleWidgetType } from 'src/models/bundle';
import {
  CREATE_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
  CREATE_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
  CREATE_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
  DELETE_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
  DELETE_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
  DELETE_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
  GET_PREDICTIVE_BUNDLE_LIST_FAILURE,
  GET_PREDICTIVE_BUNDLE_LIST_REQUEST,
  GET_PREDICTIVE_BUNDLE_LIST_SUCCESS,
  GET_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
  GET_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
  GET_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
  SYNC_BUNDLE_WIDGETS_FAILURE,
  SYNC_BUNDLE_WIDGETS_REQUEST,
  SYNC_BUNDLE_WIDGETS_SUCCESS,
  UPDATE_PREDICTIVE_BUNDLE_WIDGET_ACTION_FAILURE,
  UPDATE_PREDICTIVE_BUNDLE_WIDGET_ACTION_REQUEST,
  UPDATE_PREDICTIVE_BUNDLE_WIDGET_ACTION_SUCCESS,
  UPDATE_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
  UPDATE_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
  UPDATE_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
  GET_PREDICTIVE_BUNDLE_DEFAULT_DESIGN_SETTINGS_REQUEST,
  GET_PREDICTIVE_BUNDLE_DEFAULT_DESIGN_SETTINGS_SUCCESS,
  GET_PREDICTIVE_BUNDLE_DEFAULT_DESIGN_SETTINGS_FAILURE,
  RESET_PREDICTIVE_BUNDLE_WIDGET
} from 'stores/flow-type/bundle.type';

import apiService from 'src/services/api-service';

import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get list dynamic bundle
 * @return {object} The RSAA object for API middleware
 */
export const getPredictiveBundleList = (i18n: I18n, queries = {}) => {
  return apiService.sendRequest(
    'admin-v3.recommendation.get-widgets',
    {
      queries: {
        ...queries,
        recommendationType: 'dynamic-bundle'
      }
    },
    [
      GET_PREDICTIVE_BUNDLE_LIST_REQUEST,
      {
        type: GET_PREDICTIVE_BUNDLE_LIST_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_PREDICTIVE_BUNDLE_LIST_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.getBundleList_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get dynamic bundle widget BY Id
 * @return {object} The RSAA object for API middleware
 */
export const getWidget = (widgetId: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.get-widget', { params: { widgetId } }, [
    GET_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
    {
      type: GET_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.getBundleWidget_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to add new dynamic bundle widget
 * @return {object} The RSAA object for API middleware
 */
export const createWidget = (bundle: PredictiveBundleWidgetType, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.create-widget', { body: bundle }, [
    CREATE_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
    {
      type: CREATE_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        const { widgetSettings = {} } = data;

        // Track Full Story event
        trackFSEvent('Add new bundle offer', {
          feature: 'Recommendation',
          bundleName: bundle?.widgetName,
          discountType: bundle?.discountType,
          bundlePlacement: bundle?.placement,
          bundleStatus: bundle?.bundleStatus,
          widgetId: widgetSettings?.id
        });

        return { data };
      }
    },
    {
      type: CREATE_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.createBundleWidget_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * call to delete dynamic bundle widget
 * @return {object} The RSAA object for API middleware
 */
export const deleteWidget = (widgetId: string, i18n: I18n) => {
  const params = { widgetId };
  return apiService.sendRequest('admin-v3.recommendation.delete-widget', { params }, [
    DELETE_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
    {
      type: DELETE_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.Recommendation.Actions.deleteBundleWidget_Success'),
          i18n
        );

        const listBundle = _.get(state, `bundle.list`, []);
        const bundle = _.find(listBundle, item => item.id === widgetId);

        trackFSEvent('Deleted bundle offer', {
          feature: 'Recommendation',
          bundleName: bundle?.widgetName,
          discountType: bundle?.discountType,
          bundlePlacement: bundle?.placement,
          bundleStatus: bundle?.bundleStatus,
          widgetId: bundle?.id
        });

        return { data, widgetId };
      }
    },
    {
      type: DELETE_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.deleteBundleWidget_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to update widget
 * @params widgetStatus
 * @params i18n
 * @return {object} The RSAA object for API middleware
 */
export const updateWidget = (
  widgetId: string,
  data: PredictiveBundleWidgetType,
  meta: Object,
  i18n: I18n
) => {
  const params = { widgetId };
  return apiService.sendRequest('admin-v3.recommendation.update-widget', { params, body: data }, [
    UPDATE_PREDICTIVE_BUNDLE_WIDGET_REQUEST,
    {
      type: UPDATE_PREDICTIVE_BUNDLE_WIDGET_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        // Track Full Story event
        trackFSEvent('Edit bundle offer', {
          feature: 'Recommendation',
          bundleName: data?.widgetSettings?.widgetName,
          discountType: data?.widgetSettings?.discountType,
          bundlePlacement: data?.widgetSettings?.placement,
          bundleStatus: data?.widgetSettings?.bundleStatus,
          widgetId
        });

        return { data };
      },
      meta
    },
    {
      type: UPDATE_PREDICTIVE_BUNDLE_WIDGET_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.updateBundleWidget_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * call to update widget action
 * @params widgetId
 * @params actionType
 * @params meta
 * @params i18n
 * @return {object} The RSAA object for API middleware
 */
export const updateWidgetAction = (
  widgetId: string,
  actionType: string,
  meta: Object,
  i18n: I18n
) => {
  const params = { widgetId };
  return apiService.sendRequest(
    'admin-v3.recommendation.update-widget-action',
    { params, body: { actionType } },
    [
      UPDATE_PREDICTIVE_BUNDLE_WIDGET_ACTION_REQUEST,
      {
        type: UPDATE_PREDICTIVE_BUNDLE_WIDGET_ACTION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          if (actionType === 'pause') {
            const message = i18n.translate(
              'AdminHTML.Recommendation.Actions.updateRecommendationWidgetAction_Pause_Success'
            );
            await showSABToast(message, false);
          }

          // Track Full Story event
          trackFSEvent(`${actionType[0]?.toUpperCase()}${actionType?.slice(1)} Predictive Bundle`, {
            feature: 'Predictive Bundle'
          });

          return { data };
        },
        meta
      },
      {
        type: UPDATE_PREDICTIVE_BUNDLE_WIDGET_ACTION_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.updateBundleWidgetAction_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};
/**
 * Sync live theme to update widget status
 * @return {object} The RSAA object for API middleware
 */
export const syncWidgets = (meta: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.recommendation.sync-widgets', {}, [
    {
      type: SYNC_BUNDLE_WIDGETS_REQUEST,
      payload: async (action, state, res) => {
        await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.Recommendation.Actions.syncWidgets_Request'),
          i18n
        );
        return {};
      }
    },
    {
      type: SYNC_BUNDLE_WIDGETS_SUCCESS,
      payload: async (action, state, res) => {
        return await getJSON(res);
      },
      meta
    },
    {
      type: SYNC_BUNDLE_WIDGETS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Recommendation.Actions.syncWidgets_Failure'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * get default design settings
 * @return {object} The RSAA object for API middleware
 */
export const getDefaultDesignSettings = (i18n: I18n) => {
  const queries = { isBundle: true };
  return apiService.sendRequest(
    'admin-v3.recommendation.get-default-design-settings',
    {
      queries
    },
    [
      GET_PREDICTIVE_BUNDLE_DEFAULT_DESIGN_SETTINGS_REQUEST,
      {
        type: GET_PREDICTIVE_BUNDLE_DEFAULT_DESIGN_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          return await getJSON(res);
        }
      },
      {
        type: GET_PREDICTIVE_BUNDLE_DEFAULT_DESIGN_SETTINGS_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Recommendation.Actions.getDefaultDesignSettings_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to reset dynamic bundle rule
 * @return {object} The RSAA object for API middleware
 */
export const resetPredictiveBundle = () => {
  return { type: RESET_PREDICTIVE_BUNDLE_WIDGET, payload: {} };
};
